import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Home.less';
import { Card, Col, Empty, Row, Typography } from 'antd';
import { localStorageGet } from '@core/cookie';
import { get } from '@core/request';
import { getMyApprovalListApi } from '@services/SereviseConst';
import { numberProject } from '@utils/utils';

const { Text } = Typography;

/** 与 layouts/data/menuItems.jsx 侧边栏模块图标 class 保持一致 */
function ModuleIcon({ iconClass }) {
    return <span className={`home-module-icon bi ${iconClass}`} aria-hidden="true" />;
}

function ModuleCardLink({ to, title, description, iconClass, accent }) {
    const wrapClass = accent ? `module-icon-wrap module-icon-wrap--${accent}` : 'module-icon-wrap';
    return (
        <Link to={to} className="home-module-link">
            <Card variant="borderless" size="small" className="module cursor-pointer">
                <div className="module-inner">
                    <span className={wrapClass}>
                        <ModuleIcon iconClass={iconClass} />
                    </span>
                    <div className="module-title">{title}</div>
                    <p className="module-desc">{description}</p>
                </div>
            </Card>
        </Link>
    );
}

// 待处理任务列表组件
const PendingTasksList = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [batchList, setBatchList] = useState([]);
    const timerRef = useRef(null);

    useEffect(() => {
        const fetchPendingBatches = async () => {
            try {
                const res = await getMyApprovalListApi();
                if (res && res.status_code === 200 && res.data) {
                    const dataList = res.data || [];
                    setBatchList(dataList);
                }
            } catch (error) {
                console.error('获取待处理批次列表失败:', error);
            }
        };

        // 首次加载
        fetchPendingBatches();

        // 设置定时器，每10秒轮询一次
        timerRef.current = setInterval(fetchPendingBatches, 10000);

        // 清理定时器
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    // 获取状态颜色
    const getStatusDisplay = approvalStatus => {
        // approvalStatus: 0-未开始(灰色), 1-待提交(黄色), 2-已提交(绿色), 3-不同意(红色)
        const statusMap = {
            0: { color: '#999999' },
            1: { color: '#FA8C16' },
            2: { color: '#52C41A' },
            3: { color: '#FF4D4F' },
        };
        return statusMap[approvalStatus] || { color: '#FA8C16' };
    };

    const handleBatchClick = batch => {
        // 根据 nodeId 判断打开的弹框类型，在 URL 中添加时间戳，确保每次点击都是不同的 URL
        const timestamp = Date.now();

        // 记录来源页，关闭审批后返回（首页入口则回到 /）
        const fromPageQuery = `&fromPage=${encodeURIComponent(location.pathname)}`;

        if (batch) {
            if (batch.nodeId === 5 || batch.nodeId === 6) {
                // nodeId 为 5 或 6，打开审批弹框，跳转到财务中心劳务费页面
                navigate(`/finance/laborfee/projects?openApprovalBatch=true&projectId=${batch.conferenceId}&batchNo=${batch.batchNo}${fromPageQuery}&t=${timestamp}`);
            } else if (batch.nodeId === 10) {
                // nodeId 为 10，打开付款弹框，跳转到财务中心劳务费页面
                navigate(`/finance/laborfee/projects?openPaymentBatch=true&projectId=${batch.conferenceId}&batchNo=${batch.batchNo}${fromPageQuery}&t=${timestamp}`);
            } else {
                // 其他 nodeId，跳转医会议 - 劳务费管理页面
                navigate(`/conf/conferences/${batch.conferenceId}/laborfee?openPaymentBatch=true&batchNo=${batch.batchNo}${fromPageQuery}&t=${timestamp}`);
            }
        }
    };

    // 格式化数量显示，超过99显示99+
    const formatCount = count => {
        return count > 99 ? '99+' : count.toString();
    };

    const taskCount = batchList.length;

    return (
        <div className="pending-tasks-container">
            <div className="pending-tasks-header">
                <span style={{ fontWeight: 500, fontSize: 16 }}>待处理</span>
                {taskCount > 0 && <span className="pending-tasks-count">{formatCount(taskCount)}</span>}
            </div>

            {batchList?.length > 0 ? (
                <div className="pending-tasks-list">
                    {batchList.map(batch => {
                        const statusDisplay = getStatusDisplay(batch.approvalStatus);
                        const formattedDate = batch.batchCreatedAt || <Text type="secondary">—</Text>;

                        return (
                            <div key={batch.batchNo} className="pending-task-item" onClick={() => handleBatchClick(batch)}>
                                {/* 第一行：付款批次时间 */}
                                <div className="task-line-1">付款批次：{formattedDate}</div>

                                {/* 第二行：会议名称 */}
                                <div className="task-line-2">{batch.conferenceName}</div>

                                <div className="task-line-3">会议项目编号：{numberProject('YHY', batch.conferenceId)}</div>

                                {batch.nodeId === 10 && <div className="task-line-3">付款明细打印：{batch.printedAt ? <Text type="success">已打印</Text> : <Text type="warning">未打印</Text>}</div>}

                                {batch.pendingPaymentBearers?.length > 0 && <div className="task-line-3">待付款主体：{batch.pendingPaymentBearers.join('、')}</div>}

                                {/* 第四行：当前步骤（左） + 审核状态（右） */}
                                <div className="task-line-4">
                                    <span className="task-node" style={{ color: statusDisplay.color }}>
                                        {batch.nodeName}
                                    </span>
                                    <span className="task-approval-status" style={{ color: statusDisplay.color }}>
                                        {batch.approvalStatusName}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="pending-tasks-empty">
                    <Empty description="暂无待处理任务" />
                </div>
            )}
        </div>
    );
};

export default function Home() {
    const navigate = useNavigate();
    const [moduleState, setModuleState] = useState(null);
    useEffect(() => {
        const token = localStorageGet('token');
        if (!token) {
            navigate('/login');
        }
    }, [navigate]);

    useEffect(() => {
        const arr = localStorage.getItem('infos');
        if (arr) {
            const userName = JSON.parse(JSON.parse(arr).data).phone;
            get(`/enterprise/staffs/get-modules`, { userName }).then(res => {
                if (res.status_code === 200) {
                    setModuleState(res.data);
                }
            });
        }
    }, []);

    const moduleColProps = {
        xs: { span: 24 },
        sm: { span: 12 },
        md: { span: 8 },
        lg: { span: 6 },
    };

    return (
        <Row className="home-container" gutter={[16, 16]} wrap>
            {moduleState && (
                <Col xs={24} lg={{ flex: '1 1 0' }} className="home-page-main-col">
                    <div className="page-route-body">
                        <Row className="home-modules-row" gutter={[10, 10]} role="list">
                            <Col {...moduleColProps} className="home-module-col" role="listitem">
                                <ModuleCardLink to="/medproject/projects" title="医项目" description="综合项目管理" iconClass="bi-rocket-fill" accent="medproject" />
                            </Col>

                            <Col {...moduleColProps} className="home-module-col" role="listitem">
                                <ModuleCardLink to="/invitation/groups" title="医邀请" description="邀请函组件，项目专家邀请" iconClass="bi-envelope-fill" accent="invitation" />
                            </Col>

                            <Col {...moduleColProps} className="home-module-col" role="listitem">
                                <ModuleCardLink to="/wj/surveys" title="医调研" description="调研组件，问卷及调研报告" iconClass="bi-journal-text" accent="survey" />
                            </Col>

                            <Col {...moduleColProps} className="home-module-col" role="listitem">
                                <ModuleCardLink to="/conf/conferences" title="医会议" description="会议管理系统" iconClass="bi-people-fill" accent="conference" />
                            </Col>

                            <Col {...moduleColProps} className="home-module-col" role="listitem">
                                <ModuleCardLink to="/selection/projects" title="医评议" description="评议项目管理，评分投票管理，实践管理" iconClass="bi-clipboard-check" accent="selection" />
                            </Col>

                            <Col {...moduleColProps} className="home-module-col" role="listitem">
                                <ModuleCardLink to="/dc/experts" title="数据中心" description="机构库、专家库" iconClass="bi-database-fill" accent="dc" />
                            </Col>

                            {(1 || moduleState.includes('FINANCE')) && (
                                <Col {...moduleColProps} className="home-module-col" role="listitem">
                                    <ModuleCardLink to="/finance/laborfee/projects" title="财务中心" description="劳务费等管理模块" iconClass="bi-wallet2" accent="finance" />
                                </Col>
                            )}

                            {moduleState.includes('USER') && (
                                <Col {...moduleColProps} className="home-module-col" role="listitem">
                                    <ModuleCardLink to="/uc/users" title="用户中心" description="用户分群，用户数据，医疗决策人数据看板" iconClass="bi-person-circle" accent="uc" />
                                </Col>
                            )}
                        </Row>
                    </div>
                </Col>
            )}
            {moduleState && (
                <Col xs={24} lg={{ flex: '0 0 300px' }} className="home-page-side-col">
                    <div className="pending-tasks-container">
                        <Card className="pending-tasks-card">
                            <PendingTasksList />
                        </Card>
                    </div>
                </Col>
            )}
        </Row>
    );
}
